import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { DashboardLayout } from "./components/layouts/DashboardLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { LoginPage } from "./pages/LoginPage";
import { OnboardingPage } from "./pages/OnboardingPage";

// Lazy load heavy dashboard pages
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PackagesPage = lazy(() => import("./pages/PackagesPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));

import { ApiTestPage } from "./pages/ApiTestPage";
import { AuditLogsPage } from "./pages/AuditLogsPage";
// Import the rest normally for now to avoid breaking the build
import { BillingPage } from "./pages/BillingPage";
import { PoliciesPage } from "./pages/PoliciesPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TeamPage } from "./pages/TeamPage";
import { TokensPage } from "./pages/TokensPage";

// Lazy load demo pages (not critical for initial load)
const AuthCardDemo = lazy(() => import("./pages/AuthCardDemo"));
const SimpleDemo = lazy(() =>
	import("./pages/SimpleDemo").then((module) => ({
		default: module.SimpleDemo,
	})),
);

import { StandaloneDemo } from "./pages/StandaloneDemo";

// Loading component for lazy routes
const PageLoader = () => (
	<div className="flex items-center justify-center min-h-64">
		<div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full"></div>
	</div>
);

function App() {
	return (
		<AuthProvider>
			<TenantProvider>
				<Suspense fallback={<PageLoader />}>
					<Routes>
						{/* Demo routes - standalone without auth check */}
						<Route path="/auth-demo" element={<AuthCardDemo />} />
						<Route path="/demo" element={<SimpleDemo />} />
						<Route path="/standalone-demo" element={<StandaloneDemo />} />

						{/* Public routes */}
						<Route path="/login" element={<LoginPage />} />
						<Route path="/onboarding" element={<OnboardingPage />} />

						{/* Protected dashboard routes */}
						<Route
							path="/dashboard"
							element={
								<ProtectedRoute>
									<DashboardLayout />
								</ProtectedRoute>
							}
						>
							<Route
								index
								element={
									<Suspense fallback={<PageLoader />}>
										<DashboardPage />
									</Suspense>
								}
							/>
							<Route
								path="packages"
								element={
									<Suspense fallback={<PageLoader />}>
										<PackagesPage />
									</Suspense>
								}
							/>
							<Route
								path="analytics"
								element={
									<Suspense fallback={<PageLoader />}>
										<AnalyticsPage />
									</Suspense>
								}
							/>
							<Route path="billing" element={<BillingPage />} />
							<Route path="team" element={<TeamPage />} />
							<Route path="tokens" element={<TokensPage />} />
							<Route path="policies" element={<PoliciesPage />} />
							<Route path="settings" element={<SettingsPage />} />
							<Route path="api-test" element={<ApiTestPage />} />
							<Route path="logs" element={<AuditLogsPage />} />
						</Route>

						{/* Redirect root to dashboard */}
						<Route path="/" element={<Navigate to="/dashboard" replace />} />
					</Routes>
				</Suspense>
			</TenantProvider>
		</AuthProvider>
	);
}

export default App;
